import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { decryptToken } from "../utils/getToken";
import ThemeUploadModal from "./ThemeUploadModal";
import { parseThemeApiResponse, themeAdminUrl } from "./themeApiUtils";

const ThemeManagement = () => {
  const urlmain = API_BASE_URL;
  const token = decryptToken("accessToken") || "";
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchThemes = async () => {
    setLoading(true);
    try {
      const response = await fetch(themeAdminUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        setThemes(res.data || []);
      } else {
        toast.error(res.message || "Failed to load themes");
      }
    } catch (error) {
      toast.error(error.message || "Failed to load themes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThemes();
  }, []);

  const thumbUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${urlmain}${path}`;
  };

  const updateStatus = async (id, status) => {
    try {
      const response = await fetch(themeAdminUrl(`/${id}/status`), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        toast.success(res.message);
        fetchThemes();
      } else {
        toast.error(res.message || "Status update failed");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteDraft = async (theme) => {
    if (theme.slug === "beentara-storefront") {
      toast.warn("The default storefront theme cannot be removed from the catalog.");
      return;
    }
    const result = await Swal.fire({
      title: "Disable this theme?",
      text: theme.name,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Disable",
    });
    if (!result.isConfirmed) return;
    await updateStatus(theme.id, "disabled");
  };

  return (
    <>
      <div className="oms-header">
        <h1 className="dashboard-title">Theme Management</h1>
        <button className="oms-add-btn" onClick={() => setModalOpen(true)}>
          Upload Theme
        </button>
      </div>

      <p style={{ marginBottom: "12px", color: "#666" }}>
        Manage theme catalog, publish updates, and view tenant installations.
      </p>

      <div className="customer-table">
        <table>
          <thead>
            <tr>
              <th>Theme</th>
              <th>Slug</th>
              <th>Version</th>
              <th>Status</th>
              <th>Installed Sites</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>
                  Loading...
                </td>
              </tr>
            ) : themes.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>
                  No themes yet
                </td>
              </tr>
            ) : (
              themes.map((theme) => (
                <React.Fragment key={theme.id}>
                  <tr>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {theme.thumbnail && (
                          <img
                            src={thumbUrl(theme.thumbnail)}
                            alt=""
                            style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                          />
                        )}
                        <div>
                          <strong>{theme.name}</strong>
                          <div style={{ fontSize: 12, color: "#666" }}>
                            {theme.description || "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{theme.slug}</td>
                    <td>{theme.version}</td>
                    <td>
                      <span
                        style={{
                          color:
                            theme.status === "published"
                              ? "#059669"
                              : theme.status === "disabled"
                                ? "#dc2626"
                                : "#d97706",
                        }}
                      >
                        {theme.status}
                      </span>
                    </td>
                    <td>{theme.installedCount ?? 0}</td>
                    <td>
                      <button
                        type="button"
                        className="oms-add-btn"
                        style={{ marginRight: 8, padding: "4px 10px", fontSize: 12 }}
                        onClick={() =>
                          setExpandedId(expandedId === theme.id ? null : theme.id)
                        }
                      >
                        History
                      </button>
                      {theme.status !== "published" && (
                        <button
                          type="button"
                          className="oms-add-btn"
                          style={{ marginRight: 8, padding: "4px 10px", fontSize: 12 }}
                          onClick={() => updateStatus(theme.id, "published")}
                        >
                          Publish
                        </button>
                      )}
                      {theme.status === "published" && (
                        <button
                          type="button"
                          className="oms-add-btn"
                          style={{ marginRight: 8, padding: "4px 10px", fontSize: 12 }}
                          onClick={() => updateStatus(theme.id, "disabled")}
                        >
                          Disable
                        </button>
                      )}
                      <button
                        type="button"
                        className="oms-add-btn"
                        style={{ padding: "4px 10px", fontSize: 12 }}
                        onClick={() => {
                          setSelectedTheme(theme);
                          setVersionModalOpen(true);
                        }}
                      >
                        Upload Update
                      </button>
                    </td>
                  </tr>
                  {expandedId === theme.id && (
                    <tr>
                      <td colSpan="6" style={{ background: "#fafafa" }}>
                        <strong>Version history</strong>
                        <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                          {(theme.versions || []).map((v) => (
                            <li key={v.id}>
                              v{v.version} — {v.release_notes || "No release notes"}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <ThemeUploadModal
          token={token}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            fetchThemes();
          }}
        />
      )}

      {versionModalOpen && selectedTheme && (
        <ThemeUploadModal
          token={token}
          theme={selectedTheme}
          isVersion
          onClose={() => {
            setVersionModalOpen(false);
            setSelectedTheme(null);
          }}
          onSaved={() => {
            setVersionModalOpen(false);
            setSelectedTheme(null);
            fetchThemes();
          }}
        />
      )}
    </>
  );
};

export default ThemeManagement;
